// src/pages/admin/AdminSettings.jsx

import React, { useState, useEffect, useContext } from 'react';
import { FaCog, FaSave, FaWallet, FaDatabase, FaIdCard } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import { AppContext } from '../../context/AppContext';

import TabEvent from '../../components/settings/TabEvent';
import TabFinance from '../../components/settings/TabFinance';
import TabMasterData from '../../components/settings/TabMasterData';
import TabIdCard from '../../components/settings/TabIdCard';

const defaultConfig = {
    event_name: '',
    event_description: '',
    web_logo: '',
    event_date: '',
    location: '',
    contact_person: '',
    is_registration_open: true,
    is_public_leaderboard: true,

    proposal_title: '',
    proposal_description: '',
    invitation_title: '',
    invitation_description: '',

    important_notes: [],

    prices: {
        team_registration: 0,
        kyorugi: 0,
        poomsae_individual: 0,
        poomsae_freestyle: 0,
        poomsae_pair: 0,
        poomsae_team: 0
    },

    bank_info: {
        bank_name: '',
        account_number: '',
        account_holder: ''
    },

    weight_tolerance: 0.5
};

const defaultMasterData = {
    event_year: new Date().getFullYear(),
    belt_levels: [
        'Putih',
        'Kuning',
        'Kuning Strip',
        'Hijau',
        'Hijau Strip',
        'Biru',
        'Biru Strip',
        'Merah',
        'Merah Strip',
        'Poom',
        'Hitam'
    ],
    divisions: [],
    weight_classes: {},
    days: 1,
    arenasPerDay: [3]
};

const AdminSettings = () => {
    const { reloadSettings } = useContext(AppContext);

    const [activeTab, setActiveTab] = useState('event');
    const [isLoading, setIsLoading] = useState(false);

    const [proposalFile, setProposalFile] = useState(null);
    const [invitationFile, setInvitationFile] = useState(null);
    const [webLogoFile, setWebLogoFile] = useState(null);

    const [config, setConfig] = useState(defaultConfig);
    const [masterData, setMasterData] = useState(defaultMasterData);

    useEffect(() => {
        getSettings();
    }, []);

    const parseConfig = (value) => {
        if (!value) return {};

        if (typeof value === 'string') {
            try {
                return JSON.parse(value);
            } catch (error) {
                return {};
            }
        }

        return value;
    };

    const getSettings = async () => {
        try {
            const [res1, res2] = await Promise.all([
                api.get('/settings/1'),
                api.get('/settings/2')
            ]);

            const settingConfig = parseConfig(res1.data?.config);
            const settingMaster = parseConfig(res2.data?.config);
            const settingPrices = settingConfig.prices || {};

            setConfig(prev => ({
                ...prev,
                ...settingConfig,

                prices: {
                    ...prev.prices,
                    ...settingPrices,

                    team_registration:
                        settingPrices.team_registration ??
                        prev.prices.team_registration ??
                        0,

                    poomsae_freestyle:
                        settingPrices.poomsae_freestyle ??
                        settingPrices.poomsae_individual ??
                        prev.prices.poomsae_freestyle ??
                        0
                },

                bank_info: {
                    ...prev.bank_info,
                    ...(settingConfig.bank_info || {})
                },

                important_notes: Array.isArray(settingConfig.important_notes)
                    ? settingConfig.important_notes
                    : []
            }));

            setMasterData(prev => ({
                ...prev,
                ...settingMaster,

                belt_levels:
                    Array.isArray(settingMaster.belt_levels) && settingMaster.belt_levels.length > 0
                        ? settingMaster.belt_levels
                        : prev.belt_levels,

                divisions: Array.isArray(settingMaster.divisions)
                    ? settingMaster.divisions
                    : prev.divisions,

                weight_classes: settingMaster.weight_classes || prev.weight_classes,
                days: settingMaster.days || prev.days || 1,
                arenasPerDay: settingMaster.arenasPerDay || prev.arenasPerDay || [3]
            }));
        } catch (error) {
            toast.error('Gagal memuat pengaturan.');
        }
    };

    const formatCurrency = (value) => {
        if (value === undefined || value === null || value === '') return '0';

        const number = parseInt(String(value).replace(/\D/g, ''), 10);

        return isNaN(number)
            ? '0'
            : new Intl.NumberFormat('id-ID').format(number);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        setConfig(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleNestedChange = (parent, key, value) => {
        setConfig(prev => ({
            ...prev,
            [parent]: {
                ...(prev[parent] || {}),
                [key]: value
            }
        }));
    };

    const handlePriceChange = (field, rawValue) => {
        const cleanValue = parseInt(
            String(rawValue)
                .replace(/\./g, '')
                .replace(/\D/g, ''),
            10
        ) || 0;

        setConfig(prev => ({
            ...prev,
            prices: {
                ...(prev.prices || {}),
                [field]: cleanValue
            }
        }));
    };

    const toggleConfig = (key) => {
        setConfig(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleMasterChange = (e) => {
        const { name, value } = e.target;

        setMasterData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleWeightClassJsonChange = (e) => {
        try {
            const parsed = JSON.parse(e.target.value);

            setMasterData(prev => ({
                ...prev,
                weight_classes: parsed
            }));
        } catch (error) {
            // Biarkan input tetap bisa diketik. Validasi tetap di TabMasterData.
        }
    };

    const handleSaveConfig = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const formData = new FormData();

            const finalConfig = {
                ...config,
                prices: {
                    team_registration: Number(config.prices?.team_registration || 0),
                    kyorugi: Number(config.prices?.kyorugi || 0),
                    poomsae_individual: Number(config.prices?.poomsae_individual || 0),
                    poomsae_freestyle: Number(config.prices?.poomsae_freestyle || 0),
                    poomsae_pair: Number(config.prices?.poomsae_pair || 0),
                    poomsae_team: Number(config.prices?.poomsae_team || 0)
                },
                bank_info: {
                    bank_name: config.bank_info?.bank_name || '',
                    account_number: config.bank_info?.account_number || '',
                    account_holder: config.bank_info?.account_holder || ''
                },
                important_notes: Array.isArray(config.important_notes)
                    ? config.important_notes
                    : []
            };

            Object.keys(finalConfig).forEach(key => {
                if (typeof finalConfig[key] === 'object' && finalConfig[key] !== null) {
                    formData.append(key, JSON.stringify(finalConfig[key]));
                } else {
                    formData.append(key, finalConfig[key]);
                }
            });

            formData.set('is_registration_open', finalConfig.is_registration_open ? '1' : '0');
            formData.set('is_public_leaderboard', finalConfig.is_public_leaderboard ? '1' : '0');

            if (proposalFile) formData.append('proposal_file', proposalFile);
            if (invitationFile) formData.append('invitation_file', invitationFile);
            if (webLogoFile) formData.append('web_logo', webLogoFile);

            await api.patch('/settings/1', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const finalMasterData = {
                ...masterData,
                belt_levels: Array.isArray(masterData.belt_levels)
                    ? masterData.belt_levels
                    : defaultMasterData.belt_levels,
                divisions: Array.isArray(masterData.divisions)
                    ? masterData.divisions
                    : [],
                weight_classes: masterData.weight_classes || {},
                days: parseInt(masterData.days, 10) || 1,
                arenasPerDay: Array.isArray(masterData.arenasPerDay)
                    ? masterData.arenasPerDay
                    : [3]
            };

            await api.patch('/settings/2', finalMasterData);

            toast.success('Semua pengaturan berhasil disimpan!');

            setProposalFile(null);
            setInvitationFile(null);
            setWebLogoFile(null);

            if (reloadSettings) reloadSettings();

            getSettings();
        } catch (error) {
            toast.error(error.response?.data?.msg || 'Gagal menyimpan pengaturan.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="container-fluid p-0">
            <h2 className="fw-bold text-dark mb-4">Pengaturan Sistem</h2>

            <div className="row g-4">
                <div className="col-lg-3">
                    <div
                        className="card border-0 shadow-sm"
                        style={{
                            background: 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: '20px'
                        }}
                    >
                        <div className="card-body p-2">
                            <div className="nav flex-column nav-pills gap-1">
                                <button
                                    type="button"
                                    className={`nav-link d-flex align-items-center gap-3 px-3 py-3 rounded-3 text-start fw-bold ${activeTab === 'event' ? 'active-tab' : 'text-secondary'}`}
                                    onClick={() => setActiveTab('event')}
                                    style={activeTab === 'event' ? styles.activeTab : {}}
                                >
                                    <FaCog /> Event & Proposal
                                </button>

                                <button
                                    type="button"
                                    className={`nav-link d-flex align-items-center gap-3 px-3 py-3 rounded-3 text-start fw-bold ${activeTab === 'finance' ? 'active-tab' : 'text-secondary'}`}
                                    onClick={() => setActiveTab('finance')}
                                    style={activeTab === 'finance' ? styles.activeTab : {}}
                                >
                                    <FaWallet /> Keuangan & Aturan
                                </button>

                                <button
                                    type="button"
                                    className={`nav-link d-flex align-items-center gap-3 px-3 py-3 rounded-3 text-start fw-bold ${activeTab === 'master' ? 'active-tab' : 'text-secondary'}`}
                                    onClick={() => setActiveTab('master')}
                                    style={activeTab === 'master' ? styles.activeTab : {}}
                                >
                                    <FaDatabase /> Master Data
                                </button>

                                <button
                                    type="button"
                                    className={`nav-link d-flex align-items-center gap-3 px-3 py-3 rounded-3 text-start fw-bold ${activeTab === 'idcard' ? 'active-tab' : 'text-secondary'}`}
                                    onClick={() => setActiveTab('idcard')}
                                    style={activeTab === 'idcard' ? styles.activeTab : {}}
                                >
                                    <FaIdCard /> Template ID Card
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-lg-9">
                    {activeTab !== 'idcard' ? (
                        <form onSubmit={handleSaveConfig}>
                            {activeTab === 'event' && (
                                <TabEvent
                                    config={config}
                                    handleChange={handleChange}
                                    toggleConfig={toggleConfig}
                                    setProposalFile={setProposalFile}
                                    setInvitationFile={setInvitationFile}
                                    setWebLogoFile={setWebLogoFile}
                                    setConfig={setConfig}
                                />
                            )}

                            {activeTab === 'finance' && (
                                <TabFinance
                                    config={config}
                                    handleNestedChange={handleNestedChange}
                                    handlePriceChange={handlePriceChange}
                                    handleChange={handleChange}
                                    formatCurrency={formatCurrency}
                                />
                            )}

                            {activeTab === 'master' && (
                                <TabMasterData
                                    masterData={masterData}
                                    handleMasterChange={handleMasterChange}
                                    setMasterData={setMasterData}
                                    handleWeightClassJsonChange={handleWeightClassJsonChange}
                                />
                            )}

                            <div className="d-flex justify-content-end mt-4">
                                <button
                                    type="submit"
                                    className="btn btn-primary rounded-pill px-5 py-2 shadow fw-bold hover-scale"
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        'Menyimpan...'
                                    ) : (
                                        <>
                                            <FaSave className="me-2" /> Simpan Semua Pengaturan
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <TabIdCard reloadSettings={reloadSettings} />
                    )}
                </div>
            </div>
        </div>
    );
};

const styles = {
    activeTab: {
        background: 'var(--primary-gradient)',
        color: 'white',
        boxShadow: '0 4px 10px rgba(99, 102, 241, 0.3)'
    }
};

export default AdminSettings;